import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { BOOK } from "../lib/content";

const SIZES = ["text-base", "text-lg", "text-xl"];

export const ChapterReader = ({ open, onClose }) => {
  const [sizeIdx, setSizeIdx] = useState(1);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setProgress(max > 0 ? el.scrollTop / max : 0);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-md"
          onClick={onClose}
          data-testid="chapter-reader-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl h-[82vh] bg-[#12121A] border border-[#C5A059]/20 gold-frame flex flex-col"
          >
            <div className="h-[3px] bg-white/5 w-full">
              <motion.div
                className="h-full bg-[#C5A059] origin-left"
                style={{ scaleX: progress }}
                data-testid="chapter-reading-progress"
              />
            </div>
            <div className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-white/5">
              <div>
                <span className="label-eyebrow">{BOOK.chapter.label}</span>
                <h3 className="font-display font-medium uppercase text-lg sm:text-xl tracking-wide mt-1">{BOOK.chapter.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="chapter-font-decrease"
                  onClick={() => setSizeIdx((i) => Math.max(0, i - 1))}
                  disabled={sizeIdx === 0}
                  className="p-2 border border-white/10 text-[#A19F9A] hover:border-[#C5A059]/50 hover:text-[#E2C08D] disabled:opacity-30 transition-colors duration-300"
                  aria-label="Reducir tamaño de letra"
                >
                  <Minus size={14} />
                </button>
                <button
                  data-testid="chapter-font-increase"
                  onClick={() => setSizeIdx((i) => Math.min(SIZES.length - 1, i + 1))}
                  disabled={sizeIdx === SIZES.length - 1}
                  className="p-2 border border-white/10 text-[#A19F9A] hover:border-[#C5A059]/50 hover:text-[#E2C08D] disabled:opacity-30 transition-colors duration-300"
                  aria-label="Aumentar tamaño de letra"
                >
                  <Plus size={14} />
                </button>
                <button
                  data-testid="chapter-close-button"
                  onClick={onClose}
                  className="ml-2 p-2 text-[#A19F9A] hover:text-[#F2EFE9] transition-colors duration-300"
                  aria-label="Cerrar lector"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-6 sm:px-12 py-10" data-testid="chapter-content">
              <p className={`font-serif-book ${SIZES[sizeIdx]} leading-[1.9] text-[#E8E4DB] first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-[#C5A059] first-letter:font-semibold`}>
                {BOOK.chapter.paragraphs[0]}
              </p>
              {BOOK.chapter.paragraphs.slice(1).map((p, i) => (
                <p key={i} className={`font-serif-book ${SIZES[sizeIdx]} leading-[1.9] text-[#E8E4DB] mt-7`}>
                  {p}
                </p>
              ))}
              <div className="mt-14 mb-4 text-center">
                <span className="font-serif-book italic text-[#A19F9A]">El partido continúa en el libro…</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
